// src/hooks/useUsageData.js
// Hook for fetching usage and cost reporting data.
// Supports three-level drill-down: systemwide → tenant → (user - future).
// SuperAdmin only; all three endpoints return 403 for any other role.

import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';

/**
 * Builds a query string from a { startDate, endDate } period object.
 * Both values are optional; if absent the server defaults to month-to-date.
 */
const buildDateParams = (period = {}) => {
  const params = new URLSearchParams();
  if (period.startDate) params.set('startDate', period.startDate);
  if (period.endDate) params.set('endDate', period.endDate);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

const useUsageData = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  /**
   * Fetches the systemwide usage summary plus all tenant summaries.
   * GET /api/v1/usage/systemwide
   *
   * @param {Object} period  - { startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' }
   * @returns {Object}       - { period, systemSummary, tenantSummaries } from data
   */
  const fetchSystemwide = useCallback(async (period = {}) => {
    setLoading(true);
    setError(null);
    try {
      const qs = buildDateParams(period);
      const response = await apiService.get(`/api/v1/usage/systemwide${qs}`, user);
      if (!response.success) {
        throw new Error(response.error || 'Failed to load systemwide usage data');
      }
      return response.data;
    } catch (err) {
      const message = err.message || 'An unexpected error occurred';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Fetches usage summary for a single tenant plus user summaries.
   * GET /api/v1/usage/tenants/:tenantId
   *
   * @param {number} tenantId
   * @param {Object} period   - { startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' }
   * @returns {Object}        - { period, tenant, tenantSummary, userSummaries } from data
   */
  const fetchTenantUsage = useCallback(async (tenantId, period = {}) => {
    if (!tenantId) throw new Error('tenantId is required');
    setLoading(true);
    setError(null);
    try {
      const qs = buildDateParams(period);
      const response = await apiService.get(`/api/v1/usage/tenants/${tenantId}${qs}`, user);
      if (!response.success) {
        throw new Error(response.error || 'Failed to load tenant usage data');
      }
      return response.data;
    } catch (err) {
      const message = err.message || 'An unexpected error occurred';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user]);

  return {
    loading,
    error,
    clearError,
    fetchSystemwide,
    fetchTenantUsage,
  };
};

export default useUsageData;